<template>
    <div>
        <div
            :id="`map-${random}`"
            ref="mapContainer"
            :class="[modifier ? modifier : 'h-[430px]', 'w-full', 'map-container']"
        ></div>
        <div ref="hiddenContainer" style="display: none">
            <ProductCard v-if="activeProduct" :product-data="activeProduct" />
        </div>
    </div>
</template>

<script setup lang="ts">
import 'leaflet/dist/leaflet.css';
import L, { type LatLngTuple } from 'leaflet';
import type { IApiProductMap } from '@nuxt-monorepo/ui/types/components/Map/ApiProductMap';
import type { IAirportEntity, IHotelProduct, IParkingProduct } from '@nuxt-monorepo/ui/types';
import ProductCard from '@nuxt-monorepo/ui/components/Map/ProductCard.vue';

const { blok, productList, airport, modifier } = defineProps<{
    airport: IAirportEntity;
    productList: (IHotelProduct | IParkingProduct)[] | undefined;
    blok: IApiProductMap;
    modifier?: string;
}>();

const mapContainer = ref<HTMLDivElement | null>(null);
const random = Math.ceil(Math.random() * 1000);
const hiddenContainer = useTemplateRef('hiddenContainer');
const activeProduct = ref(productList?.[0]);

const isIHotelProduct = (product: IHotelProduct | IParkingProduct): product is IHotelProduct => {
    return (product as IParkingProduct).geo?.latitude === undefined;
};
const productsCoordinates: [number, number][] = (productList ?? [])?.map((product): [number, number] => {
    return [
        Number(isIHotelProduct(product) ? (product.latitude ?? product.address.latitude) : product.geo?.latitude),
        Number(isIHotelProduct(product) ? (product.longitude ?? product.address.longitude) : product.geo?.longitude),
    ];
});

const airportCoordinates: LatLngTuple | [] =
    airport?.latitude && airport?.longitude ? [+airport?.latitude, +airport?.longitude] : [];

// Custom icons for airport / car park
const airportIcon = L.icon({
    iconUrl: blok?.icon?.filename || '',
    iconAnchor: [13, 36],
    popupAnchor: [0, -37],
    className: 'leaflet-map-icon',
});
const productIcon = L.icon({
    iconUrl: blok?.productMarkerIcon?.filename || '',
    iconAnchor: [13, 36],
    popupAnchor: [0, -37],
    className: 'leaflet-map-icon',
});

// Get map center and map corners
function getMapOptions(coordinates: LatLngTuple[]): {
    center: LatLngTuple;
    corners: LatLngTuple[];
} {
    const latitude = coordinates.map((c) => c?.[0]);
    const longitude = coordinates.map((c) => c?.[1]);
    const left = Math.min(...latitude);
    const right = Math.max(...latitude);
    const top = Math.min(...longitude);
    const bottom = Math.max(...longitude);

    const centerX = (left + right) / 2;
    const centerY = (top + bottom) / 2;

    const corner1: LatLngTuple = [left, top];
    const corner2: LatLngTuple = [right, bottom];

    return {
        center: [centerX, centerY],
        corners: [corner1, corner2],
    };
}

let map: L.Map;

function setupLeafletMap() {
    // It can be empty airport coordinates OR empty products, not empty airport coordinates AND empty products
    const coordinatesArray: LatLngTuple[] = [];
    if (airportCoordinates.length) {
        coordinatesArray.push(airportCoordinates as LatLngTuple);
    }
    if (productsCoordinates?.length) {
        coordinatesArray.push(...productsCoordinates);
    }
    const { corners, center } = getMapOptions(coordinatesArray);

    // Init map
    map = L.map(mapContainer.value?.id ?? '', {
        zoom: 12,
        scrollWheelZoom: false,
        attributionControl: false,
    });

    map.on('load', () => {
        // if only 1 marker, zoom = 12 by default
        if (coordinatesArray.length > 1) setZoom();
    });
    map.setView(center);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        minZoom: 8,
        maxZoom: 19,
    }).addTo(map);

    // Set dynamic zoom to bound all markers, with extra padding
    function setZoom() {
        const zoomLevel = map.getBoundsZoom(corners, false, L.point(100, 100));
        map.setZoom(zoomLevel);
    }

    // Markers
    if (productsCoordinates?.length) {
        productList?.forEach((product, index) => {
            const marker = L.marker(productsCoordinates[index], { icon: productIcon });
            marker.addTo(map);
            marker.on('click', async () => {
                activeProduct.value = product;
                await nextTick();
                marker.bindPopup(hiddenContainer.value?.innerHTML ?? '');
                marker.openPopup();
            });
            marker.on('keydown', async (e) => {
                // Manual click listener prevent default popup closing on Esc press so should be added manually
                if (e.originalEvent.key === 'Escape') {
                    marker.closePopup();
                }
                if (e.originalEvent.key === 'Enter') {
                    activeProduct.value = product;
                    await nextTick();
                    marker.bindPopup(hiddenContainer.value?.innerHTML ?? '');
                }
            });
        });
    }
    if (airportCoordinates.length) {
        L.marker(airportCoordinates as LatLngTuple, { icon: airportIcon, interactive: false }).addTo(map);
    }
}

onMounted(() => {
    setupLeafletMap();
});
onUnmounted(() => {
    map?.remove();
});
</script>

<style scoped></style>
